import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CATEGORY_ORDER } from "../../CableTakeoffTabV3/deviceCategories";
import type { DeviceCategory } from "../../CableTakeoffTabV3/deviceCategories";
import { buildCircuitNames } from "../../CableTakeoffTabV3/circuitNames";
import type { CircuitName } from "../../CableTakeoffTabV3/circuitNames";
import {
  buildAssignableDevices,
  countCategories,
  defaultCategory,
  devicesInRect,
  nearestAssignable,
  summariseSelection,
} from "../../CableTakeoffTabV3/assignMode";
import type { ElementBox, PlanRect } from "../../CableTakeoffTabV3/assignMode";
import type { AssignDeviceBox } from "../../../ReviewPage/FloorPlanPanelNew/SvgOverlay/AssignDeviceBoxes";
import type { AssignDevicesArgs } from "../../CableTakeoffTabV3/useEditableCableTakeoff";
import { useTranslation } from "react-i18next";
import { useRequest } from "hooks/useRequest";
import { fetchCables, findPlanTakeoff } from "../../utils/fetchCables";
import { fetchCableTakeoffPaths } from "../../utils/fetchCableTakeoffPaths";
import { useEditableCableTakeoff } from "../../CableTakeoffTabV3/useEditableCableTakeoff";
import { planTrays } from "../../CableTakeoffTabV3/planTrays";
import { acceptedTrayTypes } from "../../CableTakeoffTabV3/trayVocabulary";
import { useCableDirectEdit } from "../../CableTakeoffTabV3/edits/useCableDirectEdit";
import { useCableDestinationInfo } from "../../CableTakeoffTabV3/useCableDestinationInfo";
import { useCableGaps } from "../../CableTakeoffTabV3/useCableGaps";
import {
  areRouteLinesHidden as routeLinesHiddenFor,
  isolatedCircuitId,
  visibleCablePaths,
} from "../../CableTakeoffTabV3/visibleCablePaths";
import { buildKarlSteps } from "../../CableTakeoffTabV3/karlSteps";
import { buildCircuitKeyMap } from "../../CableTakeoffTabV3/circuitKeys";
import type { CableSystem } from "../../CableTakeoffTabV3/cableSystems";
import { findBoardSuspects } from "../../CableTakeoffTabV3/boardCircuits";
import { isInsideFeedArea } from "../../CableTakeoffTabV3/feedAreas";
import type {
  BoardDevice,
  BoardSuspect,
} from "../../CableTakeoffTabV3/boardCircuits";
import type { CircuitPathsState } from "../../../ReviewPage/hooks/useCircuitCablePaths/types";
import {
  UNROUTED_REASON_KEYS,
  summariseUnrouted,
} from "../../CableTakeoffTabV3/unroutedDevices";
import { elementsOffTheRun } from "../../CableTakeoffTabV3/elementsOffTheRun";
import type { UnroutedElementCentre } from "../../CableTakeoffTabV3/unroutedDevices";
import { usePlanWalls } from "../../CableTakeoffTabV3/planWalls";
import { useTrayVerdicts } from "../../CableTakeoffTabV3/trayVerdicts";
import type { AreaPosition } from "../../CableTakeoffTabV3/areaAssign";
import {
  computeCableScalePixelsPerMeter,
  computeSourceConfidenceByName,
  computeUncertainSourceNames,
  decorateSourceMarkers,
} from "./utils";
import { SELECTED_RUN_MARK_MS } from "./constants";
import {
  runDeviceMarks,
  useRunMark,
  useStepMark,
} from "../../CableTakeoffTabV3/runMark";
import type {
  AreaDeviceSelection,
  DevicePosition,
  HeightMark,
  SelectedCableSource,
  UseCableTakeoffArgs,
  UseCableTakeoffResult,
} from "./types";

// Frozen and hoisted: consumers key memos on this, and a fresh [] per render is
// a new identity.

const NO_CIRCUITS: string[] = [];
const MARKER_LABEL_MAX = 12;

// A run with no printed number is named "SIBE·grp·Sicherheitsleuchte …"; the plan gets its last part, cut short.
const circuitMarkerLabel = ({
  circuitId,
  printed,
}: {
  circuitId: string;
  printed: string | null | undefined;
}): string => {
  if (printed) return printed;
  const tail = circuitId.split("·").pop()?.trim() || circuitId;
  return tail.length > MARKER_LABEL_MAX
    ? `${tail.slice(0, MARKER_LABEL_MAX)}…`
    : tail;
};
const NO_SUSPECTS: BoardSuspect[] = [];

const NO_NAMES: Record<string, CircuitName> = {};
const NO_MISSING_FEEDERS: Array<{ category: DeviceCategory; count: number }> =
  [];
type AssignModeState =
  | { phase: "off" }
  /** `circuitId` narrows the mode to one circuit, which is how a circuit is split. */
  | {
      phase: "on";
      selected: string[];
      circuitId: string | null;
      /** False when Karl's circuit step drives it: the plan selects, the panel keeps its run list. */
      showsPanel: boolean;
      /** The one category shown and selectable; null = the one with the most devices still open. */
      category: DeviceCategory | null;
    };

// Plan points: a click this close to a device picks it.
const ASSIGN_HIT_RADIUS = 30;
const NO_KEYS: string[] = [];

export const useCableTakeoff = ({
  projectId,
  language,
  cables,
  cablesPublished,
  selectedPlan,
  rightTab,
  imageScaleFactor,
  pageScale,
  byGroupId,
  iconByGroupId,
  routeUnderReview = null,
  circuitBoard = null,
  onlyCircuitIds = null,
}: UseCableTakeoffArgs): UseCableTakeoffResult => {
  const { t } = useTranslation();
  // Beta gate: the tab only appears when the agent published cables for this project.
  const cablesEnabled = !!cablesPublished?.enabled;

  const sourceToolbarRef = useRef<HTMLDivElement | null>(null);
  // Live SCREEN mouse position over the plan, so a source click can anchor its
  // floating toolbar at the cursor. Updated by a window listener while the tab is open.
  const cursorScreenPositionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  // Skip-first-run guard for the language re-fetch (the parent already loaded the
  // initial-language cables).
  const hasSkippedInitialLanguageFetch = useRef(false);

  const [isUnroutedListOpen, setUnroutedListOpen] = useState(false);
  const [selectedCableCircuit, setSelectedCableCircuit] = useState<
    string | null
  >(null);
  const [hoveredCableCircuit, setHoveredCableCircuit] = useState<string | null>(
    null,
  );
  const [highlightMarker, setHighlightMarker] = useState<DevicePosition | null>(
    null,
  );
  const [heightMarks, setHeightMarks] = useState<HeightMark[]>([]);
  const [selectedSource, setSelectedSource] =
    useState<SelectedCableSource | null>(null);
  // A box drawn purely to look: the devices it caught, held until the estimator
  // decides what to do with them or clears the catch.
  const [deviceSelection, setDeviceSelection] =
    useState<AreaDeviceSelection | null>(null);

  // Localized cables (cables_<lang>.json — German provenance etc.), re-fetched on
  // language change without a full reload. Falls back to the initial `cables` prop.
  const localizedCablesRequest = useRequest(fetchCables, { manual: true });
  const { run: runLocalizedCablesFetch } = localizedCablesRequest;
  const localizedCables = localizedCablesRequest.data ?? null;

  const selectedPlanTakeoff = useMemo(
    () =>
      findPlanTakeoff(
        localizedCables ?? cables ?? null,
        selectedPlan?.label ?? null,
      ),
    [localizedCables, cables, selectedPlan?.label],
  );
  const annotationKey = selectedPlanTakeoff?.annotation_key ?? null;
  const gapPlans = useMemo(
    () =>
      (localizedCables ?? cables)?.plans
        ?.filter((plan) => plan.annotation_key)
        .map((plan) => ({
          planName: plan.plan_name,
          annotationKey: plan.annotation_key as string,
        })) ?? [],
    [localizedCables, cables],
  );
  const {
    gaps: projectGaps,
    bundles,
    sldCircuits,
  } = useCableGaps({ projectId, plans: gapPlans });
  // Karl walks the estimator through the sheet that is open, so the steps come
  // from that plan's bundle alone.
  const karlSteps = useMemo(
    () =>
      buildKarlSteps({
        bundle:
          bundles.find(
            (bundle) => bundle.plan_name === selectedPlanTakeoff?.plan_name,
          ) ?? null,
        sldCircuits,
        t,
      }),
    [bundles, sldCircuits, selectedPlanTakeoff?.plan_name, t],
  );
  // Karl talks about the sheet that is open. Another plan's gaps are that
  // plan's business; a gap carrying no plan is project-wide and always applies.
  const cableGaps = useMemo(
    () =>
      projectGaps.filter(
        (gap) => !gap.plan || gap.plan === selectedPlanTakeoff?.plan_name,
      ),
    [projectGaps, selectedPlanTakeoff?.plan_name],
  );
  const isCableEditActive = rightTab === "cabletakeoff";

  // Per-plan circuit geometry from the agent's view bundle. Only surfaced for the
  // ACTIVE tab + current plan, and held back while (re)loading so switching plans
  // never flashes the previous plan's routes.
  const geometryRequest = useRequest(fetchCableTakeoffPaths, { manual: true });
  const { run: runGeometryFetch } = geometryRequest;

  const runGeometry =
    isCableEditActive && annotationKey && !geometryRequest.isLoading
      ? (geometryRequest.data ?? null)
      : null;

  const cableScalePixelsPerMeter = useMemo(
    () => computeCableScalePixelsPerMeter({ pageScale, imageScaleFactor }),
    [pageScale, imageScaleFactor],
  );

  // The trays THIS APP holds, not the agent's frozen copy of them.
  //
  // The router reads `quantity_surveyor_routes`, so drawing `bundle.tray` here
  // would show one drawing and bill another the moment anybody adds or renames
  // a tray — and a recalculate would then move metres over steel that is not on
  // screen. `routeGroups` is the same table, already in memory and in PDF
  // points, and it updates as he draws.
  //
  // PAGE 0, matching the server, which resolves the plan's lowest page index
  // when it is not given one. The cables surface has always been single-page
  // (`page_0.png` throughout); if that ever stops holding, both sides move.
  // Which drawn route types are cable TRAYS. The run classified every type it
  // saw and published the network it filtered down to, so its types are a
  // verdict; a type it never saw is admitted only when the vocabulary says so.
  // Held by the ROUTING module, not by the plan: whether cable rides a drawn
  // type is this engine's reading of it, and the route type is renamed, merged
  // and LV-matched by features that never route a cable.
  const trayVerdicts = useTrayVerdicts({
    floorPlanId: selectedPlan?.floorPlanId,
  });

  const trayTypeAllowlist = useMemo(
    () =>
      acceptedTrayTypes({
        drawnTypes: (selectedPlan?.routeGroups ?? []).map((g) => g.label),
        runAcceptedTypes: [
          ...new Set((runGeometry?.trays ?? []).map((tray) => tray.type)),
        ],
        // His own verdict, where he has given one, keyed back onto the label the
        // vocabulary reads. Stored against the route TYPE — the thing he renames
        // and merges — so it survives a re-run of the estimation.
        chosen: Object.fromEntries(
          (selectedPlan?.routeGroups ?? []).map((g) => [
            g.label,
            trayVerdicts.verdicts[g.route_type_id],
          ]),
        ),
      }),
    [selectedPlan?.routeGroups, runGeometry?.trays, trayVerdicts.verdicts],
  );

  /**
   * Every drawn type on the page and whether cable rides it — including the ones
   * it does NOT, which is the whole point. The vocabulary was right most of the
   * time and silent when it was wrong, so a tray he drew and named "Trasse EG"
   * carried nothing and said nothing.
   */
  const drawnRouteTypes = useMemo(
    () =>
      (selectedPlan?.routeGroups ?? []).map((group) => ({
        routeTypeId: group.route_type_id,
        label: group.label,
        metres: group.meters,
        carriesCable: trayTypeAllowlist.has(group.label),
        /** True only where HE said so; otherwise the vocabulary decided. */
        isHisChoice: trayVerdicts.verdicts[group.route_type_id] !== undefined,
      })),
    [selectedPlan?.routeGroups, trayTypeAllowlist, trayVerdicts.verdicts],
  );

  const cableTrays = useMemo(
    () =>
      planTrays({
        routeGroups: selectedPlan?.routeGroups,
        pageIndex: 0,
        acceptedTypes: trayTypeAllowlist,
      }),
    [selectedPlan?.routeGroups, trayTypeAllowlist],
  );

  // Nothing is routed before Karl's three steps; the first calculation is the router's, once they are done.
  const cableTakeoffGeometry = runGeometry;

  // Single-page review UI: page 0 is the page he is looking at, the same index
  // the scale and the calibration use.
  //
  // ABOVE the edit hook on purpose: the staleness mark is taken over the trays
  // AND the walls, so a wall he draws has to be in scope before the thing that
  // fingerprints it.
  const planWalls = usePlanWalls({
    floorPlanId: selectedPlan?.floorPlanId,
    pageIndex: 0,
  });

  const publishedSystemByCircuit = useMemo(() => {
    const byCircuit: Record<string, CableSystem | undefined> = {};
    (selectedPlanTakeoff?.boq_items ?? []).forEach((item) => {
      if (item.circuit) byCircuit[item.circuit] = item.system ?? undefined;
    });
    return byCircuit;
  }, [selectedPlanTakeoff]);

  const editableTakeoff = useEditableCableTakeoff({
    projectId,
    annotationKey,
    baseGeometry: cableTakeoffGeometry,
    imageScaleFactor,
    scalePxPerM: cableScalePixelsPerMeter,
    systemByCircuit: publishedSystemByCircuit,
    // Which plan's trays the router should read. Absent on no selection, and
    // the recalculate refuses rather than guessing a plan.
    floorPlanId: selectedPlan?.floorPlanId,
    trays: cableTrays,
    walls: planWalls.walls,
    areTrayInputsLoaded: trayVerdicts.areVerdictsLoaded,
    hasDetectedWalls: planWalls.detectedLayout.phase === "loaded",
    areWallInputsLoaded: planWalls.detectedLayout.phase !== "loading",
  });
  const effectiveCableGeometry =
    editableTakeoff.derivedGeometry ?? cableTakeoffGeometry;
  // Until the three steps are done nothing is routed, so the plan shows only boards, zones and trays.
  const isBoardsStep =
    Boolean(effectiveCableGeometry) && !editableTakeoff.isSetupDone;

  const circuitLabels = useMemo(
    () => buildCircuitKeyMap({ steps: karlSteps }),
    [karlSteps],
  );

  const circuitNames = useMemo(() => {
    const geometry = effectiveCableGeometry;
    if (!geometry) return NO_NAMES;
    const types = cableTakeoffGeometry?.deviceTypeByElementId ?? {};
    const boardNameOf = (label: string | null) =>
      label
        ? editableTakeoff.sourceNames[label] ||
          geometry.sources.find((source) => source.label === label)?.name ||
          label
        : "";
    const ids = [
      ...new Set([
        ...Object.keys(geometry.byCircuit),
        ...Object.keys(editableTakeoff.addedCircuits),
      ]),
    ];
    return buildCircuitNames({
      circuits: ids.map((id) => {
        const typeCounts: Record<string, number> = {};
        (geometry.byCircuit[id]?.paths ?? []).forEach((path) => {
          const type = (path.elementId && types[path.elementId]?.name) || "";
          if (type) typeCounts[type] = (typeCounts[type] ?? 0) + 1;
        });
        const boardLabel = editableTakeoff.feedingBoardByCircuit[id] ?? null;
        return {
          id,
          boardLabel,
          boardName: boardNameOf(boardLabel),
          category: editableTakeoff.categoryByCircuit[id] ?? null,
          printedKey:
            editableTakeoff.circuitKeyOverrides[id] ??
            circuitLabels[id] ??
            null,
          createdKey: editableTakeoff.addedCircuits[id]?.key ?? null,
          typeCounts,
        };
      }),
      custom: editableTakeoff.circuitNames,
      moreTypes: (count) =>
        t("smartSurveyor.cablesBeta.circuitMoreTypes", { count }),
    });
  }, [
    effectiveCableGeometry,
    cableTakeoffGeometry,
    editableTakeoff.sourceNames,
    editableTakeoff.addedCircuits,
    editableTakeoff.feedingBoardByCircuit,
    editableTakeoff.categoryByCircuit,
    editableTakeoff.circuitNames,
    editableTakeoff.circuitKeyOverrides,
    circuitLabels,
    t,
  ]);

  const devicesByCircuit = useMemo(() => {
    const byCircuit: Record<string, BoardDevice[]> = {};
    Object.entries(effectiveCableGeometry?.byCircuit ?? {}).forEach(
      ([circuitId, state]) => {
        byCircuit[circuitId] = state.paths.flatMap((path) => {
          const end = path.waypoints[path.waypoints.length - 1];
          return end
            ? [
                {
                  destinationId: path.destinationId,
                  position: { x: end.x, y: end.y },
                },
              ]
            : [];
        });
      },
    );
    return byCircuit;
  }, [effectiveCableGeometry]);

  const {
    feedingBoardByCircuit,
    areaFeeds,
    removedSources,
    systemByCircuit,
    categoryByCircuit,
    feedsByBoard,
  } = editableTakeoff;
  const circuitBoardLabel = circuitBoard?.label ?? null;
  const busiestBoard = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(feedingBoardByCircuit).forEach((board) => {
      if (board) counts[board] = (counts[board] ?? 0) + 1;
    });
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] ?? null;
  }, [feedingBoardByCircuit]);
  const isCircuitBoardMain =
    circuitBoard?.isMain ?? circuitBoardLabel === busiestBoard;

  const boardCircuitIds = useMemo(
    () =>
      circuitBoardLabel
        ? Object.keys(feedingBoardByCircuit).filter(
            (circuitId) =>
              feedingBoardByCircuit[circuitId] === circuitBoardLabel,
          )
        : NO_CIRCUITS,
    [circuitBoardLabel, feedingBoardByCircuit],
  );

  const boardSuspects = useMemo(
    () =>
      circuitBoardLabel
        ? findBoardSuspects({
            board: circuitBoardLabel,
            isMain: isCircuitBoardMain,
            circuitIds: boardCircuitIds,
            devicesByCircuit,
            feedingBoardByCircuit,
            systemByCircuit,
            zones: areaFeeds,
            isSourceLive: (label) => !removedSources[label],
            zoneTakes: (zoneBoard, circuitId) => {
              const category = categoryByCircuit[circuitId];
              if (!category)
                return (systemByCircuit[circuitId] ?? "power") === "power";
              return Boolean(
                feedsByBoard[zoneBoard]?.feed.categories.includes(category),
              );
            },
          })
        : NO_SUSPECTS,
    [
      circuitBoardLabel,
      isCircuitBoardMain,
      boardCircuitIds,
      devicesByCircuit,
      feedingBoardByCircuit,
      systemByCircuit,
      areaFeeds,
      removedSources,
      categoryByCircuit,
      feedsByBoard,
    ],
  );

  // One board's devices, each in its circuit's colour and carrying its number; strangers in its zone come too.
  const boardPaths = useMemo<CircuitPathsState | null>(() => {
    if (!circuitBoardLabel || !effectiveCableGeometry) return null;
    const strangers = new Set(
      boardSuspects
        .filter((suspect) => suspect.reason === "foreignInZone")
        .map((suspect) => suspect.destinationId),
    );
    const owned = new Set(boardCircuitIds);
    const paths = Object.entries(effectiveCableGeometry.byCircuit).flatMap(
      ([circuitId, state]) =>
        state.paths
          .filter(
            (path) => owned.has(circuitId) || strangers.has(path.destinationId),
          )
          .map((path) => ({
            ...path,
            markerLabel: circuitMarkerLabel({
              circuitId,
              printed: circuitLabels[circuitId],
            }),
          })),
    );
    return { paths, distributionBoardPosition: null };
  }, [
    circuitBoardLabel,
    effectiveCableGeometry,
    boardCircuitIds,
    boardSuspects,
    circuitLabels,
  ]);

  const uncertainSourceNames = useMemo(
    () =>
      computeUncertainSourceNames(
        selectedPlanTakeoff?.provenance?.sources ?? [],
      ),
    [selectedPlanTakeoff],
  );
  const sourceConfidenceByName = useMemo(
    () =>
      computeSourceConfidenceByName(
        selectedPlanTakeoff?.provenance?.sources ?? [],
      ),
    [selectedPlanTakeoff],
  );
  const cableSourceMarkers = useMemo(() => {
    const sources = effectiveCableGeometry?.sources;
    const markers = decorateSourceMarkers({
      sources,
      uncertainSourceNames,
      confidenceByBoardName: sourceConfidenceByName,
      sourceNames: editableTakeoff.sourceNames,
    });
    if (!markers || !circuitBoardLabel) return markers;
    // Decorated in the sources' own order, so the raw label is read by index.
    return markers.map((marker, index) =>
      sources?.[index]?.label === circuitBoardLabel
        ? { ...marker, isHighlighted: true }
        : marker,
    );
  }, [
    effectiveCableGeometry?.sources,
    uncertainSourceNames,
    sourceConfidenceByName,
    editableTakeoff.sourceNames,
    circuitBoardLabel,
  ]);

  const clearDeviceSelection = useCallback(() => setDeviceSelection(null), []);

  // Commits a held selection onto one circuit. Separate from the catch on
  // purpose: the estimator sees what the box caught, then decides.
  const assignDeviceSelectionTo = useCallback(
    (circuitId: string) => {
      setDeviceSelection((selection) => {
        if (!selection || !circuitId) return selection;
        selection.devices
          .filter((device) => device.fromCircuitId !== circuitId)
          .forEach((device) =>
            editableTakeoff.reassignDevice(device.destinationId, circuitId),
          );
        return null;
      });
    },
    [editableTakeoff],
  );

  // Devices the estimator asked to SEE — hovering a removal, say. They ring and
  // pulse exactly like a box catch, because they are the same question ("which
  // ones are those?") asked from the list instead of the drawing.
  // One mode for putting devices on circuits: the plan shows devices, not cable, and a drag selects.
  const [assignMode, setAssignMode] = useState<AssignModeState>({
    phase: "off",
  });
  const isAssigning = assignMode.phase === "on";
  const splittingCircuit =
    assignMode.phase === "on" ? assignMode.circuitId : null;
  const [spotlightDevices, setSpotlightDevices] = useState<
    DevicePosition[] | null
  >(null);

  const selectedDeviceMarkers = useMemo(
    () =>
      spotlightDevices ??
      deviceSelection?.devices.map((device) => device.position),
    [spotlightDevices, deviceSelection],
  );

  // Read through a ref so selecting a run stays one stable callback: it is the
  // plan's click handler and the list's, and a fresh arrow per geometry change
  // would rebuild both on every edit.
  const geometryRef = useRef(effectiveCableGeometry);
  geometryRef.current = effectiveCableGeometry;
  /**
   * Mark the run he has just picked, or the one a device has just joined, on the
   * plan exactly as it stands.
   *
   * This is the whole difference between a SELECTION and Karl's own focus. Karl
   * frames the run he is asking about, because the estimator did not choose it
   * and may be looking anywhere on the sheet. A selection — and a confirmation —
   * is the estimator's own act: what it owes him is "here it is", not a new
   * viewport — *"i want on the all plan only that route blinking so i see whats
   * i selected, not the zoom part of it"*.
   */
  const markRunOnPlan = useRunMark({
    getRunMarks: (circuitId) =>
      runDeviceMarks({ geometry: geometryRef.current, circuitId }),
    setMarks: setSpotlightDevices,
    dwellMs: SELECTED_RUN_MARK_MS,
  });

  /**
   * He acted in the panel; the drawing says where it landed. Every way a device
   * joins a run raises the same token, so the confirmation is identical for all
   * of them — three different feedbacks for one event would read as three
   * different features.
   *
   * It stays visible while another run is isolated, because the marks are their
   * own layer and not the filtered cables: a confirmation he cannot see is not
   * a confirmation.
   */
  const movedTo = editableTakeoff.deviceMovedTo;
  useEffect(() => {
    if (!movedTo) return;
    markRunOnPlan(movedTo.circuitId);
  }, [movedTo, markRunOnPlan]);

  /**
   * And a step back says where IT landed, through the same marks. This reverses
   * the old rule that undo is silent: that rule was about not replaying a MOVE
   * confirmation, and on a sheet of 83 runs "the total moved" is not an answer
   * to what changed.
   */
  useStepMark({
    steppedTo: editableTakeoff.steppedTo,
    geometry: effectiveCableGeometry,
    markRunOnPlan,
  });

  const selectCableCircuit = useCallback(
    (circuitId: string | null) => {
      setSelectedCableCircuit(circuitId);
      markRunOnPlan(circuitId);
    },
    [markRunOnPlan],
  );

  const handleSelectSource = useCallback((label: string) => {
    setSelectedSource({
      label,
      screenX: cursorScreenPositionRef.current.x,
      screenY: cursorScreenPositionRef.current.y,
    });
  }, []);

  const { addAreaFeed } = editableTakeoff;
  const handleZoneDrawn = useCallback(
    (sourceLabel: string, polygon: AreaPosition[]) => {
      const [first] = polygon;
      addAreaFeed({
        id: `zone-${sourceLabel}-${Math.round(first.x)}-${Math.round(first.y)}`,
        sourceLabel,
        polygon,
      });
    },
    [addAreaFeed],
  );

  // Direct-manipulation editing: click a source or cable node to pick it up, click
  // to drop; click a route line to select it. A source click SELECTS it (opens the
  // toolbar at the cursor) rather than grabbing — the toolbar's Move grabs it.
  const cableDirectEdit = useCableDirectEdit({
    edit: editableTakeoff,
    geometry: effectiveCableGeometry,
    // Clicking a cable on the drawing is a deliberate selection too, so it
    // marks the run it isolated. It moves no viewport: he is looking at the
    // place he clicked.
    onSelectCircuit: selectCableCircuit,
    onSelectSource: handleSelectSource,
    // The circuit the estimator is actually working, which is Karl's route
    // while he is walking one. Keying this on `selectedCableCircuit` alone
    // meant a node of Karl's isolated run was never grabbable — the click
    // merely selected the circuit it was already showing.
    selectedCircuit: routeUnderReview ?? selectedCableCircuit,
    // A dragged path used to be stored against the (circuitId, destIndex) it was
    // grabbed at, so after any reassignment it landed on whichever device now sat
    // at that slot; the override is keyed on the path's own destinationId now.
    routeEditingEnabled: true,
    onZoneDrawn: handleZoneDrawn,
    onWallDrawn: (kind, points) => void planWalls.addWall(kind, points),
  });

  // What he has picked rings and pulses while he picks; the SELECTION is only
  // published when he confirms, so the ask stays armed until he is done rather
  // than settling on his first click.
  const pickedPositions = cableDirectEdit.pickedDevices;
  useEffect(() => {
    if (!cableDirectEdit.isPicking) return;
    setSpotlightDevices(
      pickedPositions.length
        ? pickedPositions.map((device) => device.position)
        : null,
    );
  }, [cableDirectEdit.isPicking, pickedPositions]);

  // Only while a pick is armed: rings on every device the rest of the time
  // would be noise on a plan that already carries 569 of them. A board open in
  // the list rang its suspects too, for Karl's circuit step; with that step gone
  // they stayed on screen beside whatever run he selected, saying nothing.
  const candidateDeviceMarkers = useMemo(
    () =>
      cableDirectEdit.isPicking
        ? cableDirectEdit.candidateDevices.map((device) => device.position)
        : undefined,
    [cableDirectEdit.isPicking, cableDirectEdit.candidateDevices],
  );

  // Bbox centres in PDF points, the same space the device positions are in —
  // this is how `useCableDestinationInfo` already names and illustrates every
  // routed destination, and an unrouted device is named by exactly that rule.
  const unroutedElementCentres = useMemo<UnroutedElementCentre[]>(() => {
    const centres: UnroutedElementCentre[] = [];
    for (const [groupId, elements] of Object.entries(byGroupId)) {
      const iconDataUrl = iconByGroupId.get(groupId);
      for (const element of elements) {
        const bbox = element.bbox;
        if (!bbox) continue;
        centres.push({
          groupId,
          x: (bbox.x1 + bbox.x2) / 2,
          y: (bbox.y1 + bbox.y2) / 2,
          iconDataUrl,
        });
      }
    }
    return centres;
  }, [byGroupId, iconByGroupId]);

  // The run has no idea he adopted anything, so it keeps publishing these in
  // `devices_without_circuit`. The set is handed to the summary rather than
  // filtered by each reader of it.
  // Trays default ON: he asked to see them, and a layer he has to switch on to
  // check the routing is a layer he will forget exists.
  const [areTraysShown, setTraysShown] = useState(true);
  const [focusedTrayType, setFocusedTrayType] = useState<string | null>(null);
  const [highlightTrayType, setHighlightTrayType] = useState<string | null>(
    null,
  );

  const adoptedElementIds = useMemo(
    () => new Set(Object.keys(editableTakeoff.adoptedDevices)),
    [editableTakeoff.adoptedDevices],
  );

  // The run's roster is frozen at the moment it executed, so an element drawn
  // afterwards was on the plan and in no list that could give it a cable. These
  // carry the id and the position adoption needs, off the app's own elements.
  const offTheRunDevices = useMemo(
    () =>
      elementsOffTheRun({
        byGroupId,
        baseGeometry: cableTakeoffGeometry,
        unroutedDevices: karlSteps.unroutedDevices,
      }),
    [byGroupId, cableTakeoffGeometry, karlSteps.unroutedDevices],
  );

  // ONE list of devices that still need a circuit, whoever noticed them. Every
  // consumer below reads it, so the plan's markers and the panel's rows can
  // never disagree about which devices are still on no run.
  // A device no board reaches is on no run either: same markers, same rows, same adoption.
  const unfedDevices = editableTakeoff.unfedDevices;
  const devicesNeedingCircuit = useMemo(() => {
    const unfed = unfedDevices.map((device) => ({
      id: device.elementId,
      name: device.name,
      system: device.category,
      reason: "no_feeder" as const,
      position: device.position,
    }));
    const extra = [...offTheRunDevices, ...unfed];
    return extra.length
      ? [...karlSteps.unroutedDevices, ...extra]
      : karlSteps.unroutedDevices;
  }, [karlSteps.unroutedDevices, offTheRunDevices, unfedDevices]);

  const unroutedSummary = useMemo(
    () =>
      summariseUnrouted({
        devices: devicesNeedingCircuit,
        elements: unroutedElementCentres,
        adoptedElementIds,
      }),
    [adoptedElementIds, devicesNeedingCircuit, unroutedElementCentres],
  );

  // Drawn only while their list or a board's circuits are open. Withdrawn while one
  // run is isolated or a pick is armed: both narrow the plan to one thing on purpose.
  const isInOpenBoardArea = useCallback(
    (point: { x: number; y: number }): boolean => {
      if (!circuitBoardLabel) return true;
      const zones = areaFeeds.filter(
        (area) => !removedSources[area.sourceLabel],
      );
      const inside = zones.filter((area) => isInsideFeedArea({ point, area }));
      if (isCircuitBoardMain) return inside.length === 0;
      return inside.some((area) => area.sourceLabel === circuitBoardLabel);
    },
    [circuitBoardLabel, isCircuitBoardMain, areaFeeds, removedSources],
  );

  // An open board counts and shows only the strays it could feed: a SIBE's area is not where a Rauchmelder is sorted.
  const isForOpenBoard = useCallback(
    (elementId: string): boolean => {
      const boardCategories = circuitBoardLabel
        ? feedsByBoard[circuitBoardLabel]?.feed.categories
        : undefined;
      if (!boardCategories) return true;
      const category =
        cableTakeoffGeometry?.deviceTypeByElementId?.[elementId]?.category;
      return Boolean(
        category && category !== "other" && boardCategories.includes(category),
      );
    },
    [circuitBoardLabel, feedsByBoard, cableTakeoffGeometry],
  );

  // While assigning, a stray of a category no board feeds is one row in the panel, not a dot on the plan.
  const isDrawnWhileAssigning = useCallback(
    (elementId: string): boolean => {
      if (!isAssigning) return true;
      const category =
        cableTakeoffGeometry?.deviceTypeByElementId?.[elementId]?.category;
      if (!category || category === "other") return false;
      return Object.values(feedsByBoard).some((resolved) =>
        resolved.feed.categories.includes(category),
      );
    },
    [isAssigning, cableTakeoffGeometry, feedsByBoard],
  );

  const boardUnroutedCount = useMemo(
    () =>
      circuitBoardLabel
        ? unroutedSummary.open.filter(
            (device) =>
              device.position &&
              isInOpenBoardArea(device.position) &&
              isForOpenBoard(device.id) &&
              isDrawnWhileAssigning(device.id),
          ).length
        : 0,
    [
      circuitBoardLabel,
      unroutedSummary.open,
      isInOpenBoardArea,
      isForOpenBoard,
      isDrawnWhileAssigning,
    ],
  );

  const unroutedDeviceMarkers = useMemo(() => {
    if (isBoardsStep) return undefined;
    // Shown when he is looking for them — the list is open, a board's circuits are, or he is assigning.
    // Zuordnen draws every device as its own box, the ones on no run included.
    if (isAssigning) return undefined;
    if (!isUnroutedListOpen && !circuitBoardLabel) return undefined;
    const isNarrowed =
      Boolean(routeUnderReview ?? selectedCableCircuit) ||
      cableDirectEdit.isPicking;
    if (isNarrowed && !circuitBoardLabel && !isAssigning) return undefined;
    // The SUMMARY's list, not the run's roster: a device he has adopted is no
    // longer on no run, and it kept its ring here while the panel had already
    // stopped counting it.
    return unroutedSummary.open.flatMap((device) =>
      device.position &&
      isInOpenBoardArea(device.position) &&
      isForOpenBoard(device.id)
        ? [
            {
              x: device.position.x,
              y: device.position.y,
              label: t("smartSurveyor.cablesBeta.unroutedMarkerTitle", {
                name: device.name,
                reason: t(UNROUTED_REASON_KEYS[device.reason]),
              }),
            },
          ]
        : [],
    );
  }, [
    isBoardsStep,
    circuitBoardLabel,
    isInOpenBoardArea,
    unroutedSummary.open,
    routeUnderReview,
    selectedCableCircuit,
    cableDirectEdit.isPicking,
    isUnroutedListOpen,
    isAssigning,
    isForOpenBoard,
    t,
  ]);

  const confirmDevicePick = useCallback(() => {
    const picked = cableDirectEdit.pickedDevices;
    if (!picked.length) return;
    setSpotlightDevices(null);
    setDeviceSelection({
      rect: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
      devices: picked.map((device) => ({
        destinationId: device.destinationId,
        deviceName: device.destinationId,
        fromCircuitId: device.circuitId,
        position: device.position,
      })),
      count: picked.length,
      byCircuit: [],
    });
    cableDirectEdit.cancel();
  }, [cableDirectEdit]);

  // The base geometry rides along so a device the estimator struck off — gone
  // from the derived geometry — can still be named by its own destinationId.
  const destinationInfo = useCableDestinationInfo({
    geometry: effectiveCableGeometry,
    baseGeometry: cableTakeoffGeometry,
    byGroupId,
    iconByGroupId,
  });
  const cableDestinationInfo = destinationInfo.byCircuit;
  const cableDestinationInfoById = destinationInfo.byDestinationId;

  // OFF by default — *"Showing all routes at once is somehow lagging always."*
  // 567 cables is 567 `non-scaling-stroke` paths with pointer events on them,
  // re-rasterised on every pan and hit-tested on every mouse move. The devices
  // stay on the sheet either way; this is only the cable between them.
  const [areAllCablesShown, setAllCablesShown] = useState(false);

  // Isolation beats the switch, and that rule lives beside the one that decides
  // WHICH paths are drawn — the two cannot be allowed to disagree about whether
  // a run is isolated.
  const isolation = {
    geometry: effectiveCableGeometry,
    hoveredCircuitId: cableDirectEdit.shapeEditCircuit
      ? null
      : hoveredCableCircuit,
    routeUnderReview: routeUnderReview ?? null,
    selectedCircuitId: selectedCableCircuit,
  };
  // An open board shows its devices by colour, but a run he picked inside it is shown with its cable.
  const areRouteLinesHidden =
    isBoardsStep ||
    isAssigning ||
    (Boolean(circuitBoardLabel) && !isolatedCircuitId(isolation)) ||
    routeLinesHiddenFor({ areAllCablesShown, ...isolation });

  const visiblePaths = useMemo(
    () =>
      visibleCablePaths({
        geometry: effectiveCableGeometry,
        grabbedKind: cableDirectEdit.grabbed?.kind,
        isBoardsStep,
        // Hover previews another run — except while one is open for reshaping.
        // There the preview swapped the drawn geometry out from under the mode:
        // the handles are built from the run on the plan, so brushing a row in
        // the list took the corners off the cable he was about to grab.
        hoveredCircuitId: cableDirectEdit.shapeEditCircuit
          ? null
          : hoveredCableCircuit,
        routeUnderReview: routeUnderReview ?? null,
        selectedCircuitId: selectedCableCircuit,
      }),
    [
      effectiveCableGeometry,
      isBoardsStep,
      selectedCableCircuit,
      hoveredCableCircuit,
      routeUnderReview,
      cableDirectEdit.grabbed,
      cableDirectEdit.shapeEditCircuit,
    ],
  );
  // A picked run beats the open board: only that run's cable and devices are drawn.
  const isolatedRun = isolatedCircuitId(isolation);
  const cableTakeoffPaths = useMemo(() => {
    // Zuordnen draws the devices as boxes of their own; a dot under each would be a second mark for one thing.
    if (isAssigning) return null;
    const drawn =
      (isolatedRun || isBoardsStep ? null : boardPaths) ?? visiblePaths;
    if (!drawn || !onlyCircuitIds) return drawn;
    const shown = new Set(onlyCircuitIds);
    return {
      ...drawn,
      paths: drawn.paths.filter((path) => shown.has(path.routeId)),
    };
  }, [
    isAssigning,
    isBoardsStep,
    isolatedRun,
    boardPaths,
    visiblePaths,
    onlyCircuitIds,
  ]);

  const fedCategories = useMemo(
    () =>
      new Set(
        Object.values(feedsByBoard).flatMap(
          (resolved) => resolved.feed.categories,
        ),
      ),
    [feedsByBoard],
  );
  const elementBoxes = useMemo<ElementBox[]>(
    () =>
      isAssigning
        ? Object.values(byGroupId).flatMap((elements) =>
            elements.flatMap((element) =>
              element.bbox ? [{ id: element.id, bbox: element.bbox }] : [],
            ),
          )
        : [],
    [isAssigning, byGroupId],
  );
  const allAssignableDevices = useMemo(
    () =>
      isAssigning
        ? buildAssignableDevices({
            elementBoxes,
            geometry: effectiveCableGeometry,
            unfedDevices: splittingCircuit ? [] : editableTakeoff.unfedDevices,
            unrouted: splittingCircuit ? [] : unroutedSummary.open,
            deviceTypeByElementId: cableTakeoffGeometry?.deviceTypeByElementId,
            // Strays only of categories some board feeds: the rest is one row per category, not hundreds of dots.
            acceptsCategory: (category) =>
              category !== "other" && fedCategories.has(category),
            ...(splittingCircuit
              ? {
                  isOnBoard: (circuitId: string) =>
                    circuitId === splittingCircuit,
                }
              : circuitBoardLabel
                ? {
                    isOnBoard: (circuitId: string) =>
                      feedingBoardByCircuit[circuitId] === circuitBoardLabel,
                    isInBoardArea: isInOpenBoardArea,
                    acceptsCategory: (category) =>
                      category !== "other" &&
                      Boolean(
                        feedsByBoard[
                          circuitBoardLabel
                        ]?.feed.categories.includes(category),
                      ),
                  }
                : {}),
          })
        : [],
    [
      isAssigning,
      splittingCircuit,
      effectiveCableGeometry,
      editableTakeoff.unfedDevices,
      unroutedSummary.open,
      cableTakeoffGeometry,
      circuitBoardLabel,
      feedingBoardByCircuit,
      isInOpenBoardArea,
      feedsByBoard,
      fedCategories,
      elementBoxes,
    ],
  );
  const assignCategories = useMemo(
    () => countCategories(allAssignableDevices),
    [allAssignableDevices],
  );
  const chosenCategory = assignMode.phase === "on" ? assignMode.category : null;
  const assignCategory =
    chosenCategory &&
    assignCategories.some((count) => count.category === chosenCategory)
      ? chosenCategory
      : defaultCategory(assignCategories);
  // Never two categories at once: a box dragged over a light and a SIBE light next to it takes only the shown one.
  const assignableDevices = useMemo(
    () =>
      allAssignableDevices.filter(
        (device) => device.category === assignCategory,
      ),
    [allAssignableDevices, assignCategory],
  );
  // Categories no live board feeds, and how many devices wait on each.
  const missingFeeders = useMemo(
    () =>
      isAssigning && !splittingCircuit && !circuitBoardLabel
        ? CATEGORY_ORDER.flatMap((category) => {
            if (fedCategories.has(category)) return [];
            const count =
              editableTakeoff.unfedCategories[category]?.length ?? 0;
            return count ? [{ category, count }] : [];
          })
        : NO_MISSING_FEEDERS,
    [
      isAssigning,
      splittingCircuit,
      circuitBoardLabel,
      fedCategories,
      editableTakeoff.unfedCategories,
    ],
  );
  const assignSelection = useMemo(
    () =>
      summariseSelection(
        assignableDevices,
        assignMode.phase === "on" ? assignMode.selected : [],
      ),
    [assignableDevices, assignMode],
  );

  const { cancel: cancelDirectEdit } = cableDirectEdit;
  const enterAssignMode = useCallback(
    (circuitId?: string, options?: { showsPanel?: boolean }) => {
      cancelDirectEdit();
      setSelectedCableCircuit(null);
      setAssignMode({
        phase: "on",
        selected: [],
        circuitId: circuitId ?? null,
        showsPanel: options?.showsPanel ?? true,
        category: null,
      });
    },
    [cancelDirectEdit],
  );
  const exitAssignMode = useCallback(() => setAssignMode({ phase: "off" }), []);
  const setAssignSelected = useCallback(
    (keys: string[]) =>
      setAssignMode((current) =>
        current.phase === "on" ? { ...current, selected: keys } : current,
      ),
    [],
  );
  const setAssignCategory = useCallback(
    (category: DeviceCategory) =>
      setAssignMode((current) =>
        current.phase === "on"
          ? { ...current, category, selected: [] }
          : current,
      ),
    [],
  );
  const assignDeviceBoxes = useMemo<AssignDeviceBox[] | undefined>(() => {
    if (!isAssigning) return undefined;
    const selected = new Set(
      assignMode.phase === "on" ? assignMode.selected : [],
    );
    const colourOf = (circuitId: string): string => {
      const rgb = effectiveCableGeometry?.byCircuit[circuitId]?.paths[0]?.rgb;
      return rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : "#64748b";
    };
    return assignableDevices.map((device) => ({
      key: device.key,
      bbox: device.bbox,
      color:
        device.kind === "onCircuit" ? colourOf(device.circuitId) : "#64748b",
      state: selected.has(device.key)
        ? "selected"
        : device.kind === "onCircuit"
          ? "onCircuit"
          : "open",
    }));
  }, [isAssigning, assignMode, assignableDevices, effectiveCableGeometry]);
  const toggleAssignAt = useCallback(
    (point: { x: number; y: number }) => {
      const key = nearestAssignable(
        assignableDevices,
        point,
        ASSIGN_HIT_RADIUS,
      );
      if (!key) return;
      setAssignMode((current) => {
        if (current.phase !== "on") return current;
        const selected = current.selected.includes(key)
          ? current.selected.filter((one) => one !== key)
          : [...current.selected, key];
        return { ...current, selected };
      });
    },
    [assignableDevices],
  );
  const selectAssignArea = useCallback(
    (rect: PlanRect & { isAdditive?: boolean }) => {
      const keys = devicesInRect(assignableDevices, rect);
      setAssignMode((current) => {
        if (current.phase !== "on") return current;
        const selected = rect.isAdditive
          ? [...new Set([...current.selected, ...keys])]
          : keys;
        return { ...current, selected };
      });
    },
    [assignableDevices],
  );
  const { assignDevices, removeDevices } = editableTakeoff;
  const assignSelectionTo = useCallback(
    (target: AssignDevicesArgs["target"]): string | null => {
      const circuitId = assignDevices({
        destinationIds: assignSelection.destinationIds,
        adoptions: assignSelection.adoptions,
        target,
      });
      if (circuitId) setAssignSelected([]);
      return circuitId;
    },
    [assignDevices, assignSelection, setAssignSelected],
  );
  const removeAssignSelection = useCallback(() => {
    const onRuns = assignableDevices.filter(
      (device) =>
        device.kind === "onCircuit" &&
        assignMode.phase === "on" &&
        assignMode.selected.includes(device.key),
    );
    if (!onRuns.length) return;
    removeDevices(
      onRuns.map((device) =>
        device.kind === "onCircuit" ? device.destinationId : "",
      ),
    );
    setAssignSelected([]);
  }, [assignableDevices, assignMode, removeDevices, setAssignSelected]);

  const cablesForHeader = cablesEnabled
    ? (localizedCables ?? cables ?? null)
    : null;

  // Re-fetch localized cables when the language changes (skip the first run).
  useEffect(() => {
    if (!hasSkippedInitialLanguageFetch.current) {
      hasSkippedInitialLanguageFetch.current = true;
      return;
    }
    if (!cablesEnabled) return;
    runLocalizedCablesFetch(projectId, language);
  }, [
    language,
    cablesEnabled,
    projectId,
    cablesPublished?.published_at,
    runLocalizedCablesFetch,
  ]);

  // Fetch per-plan geometry when the tab is open; reset circuit selection on change.
  useEffect(() => {
    setSelectedCableCircuit(null);
    setHoveredCableCircuit(null);
    // The catch belongs to the sheet it was drawn on.
    setDeviceSelection(null);
    if (!isCableEditActive || !annotationKey) return;
    runGeometryFetch(projectId, annotationKey, imageScaleFactor);
  }, [
    isCableEditActive,
    annotationKey,
    projectId,
    imageScaleFactor,
    runGeometryFetch,
  ]);

  // Track the SCREEN mouse position while the tab is open (anchors the toolbar).
  useEffect(() => {
    if (!isCableEditActive) return;
    const handlePointerMove = (event: MouseEvent) => {
      cursorScreenPositionRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener("mousemove", handlePointerMove);
    return () => window.removeEventListener("mousemove", handlePointerMove);
  }, [isCableEditActive]);

  // Escape cancels a pick-up, leaves the shape-edit mode, and closes the source
  // toolbar. It is the one key that always means "I did not mean that".
  // Ctrl/Cmd+Z is the other: every trade tool he has used for thirty years has
  // it, and it must not depend on finding a button first.
  useEffect(() => {
    if (!isCableEditActive) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (cableDirectEdit.zoneDraft && event.key === "Enter") {
        event.preventDefault();
        cableDirectEdit.finishZone();
        return;
      }
      if (cableDirectEdit.zoneDraft && event.key === "Escape") {
        cableDirectEdit.cancel();
        return;
      }
      if (event.key === "Escape" && isAssigning) {
        if (assignSelection.count) setAssignSelected([]);
        else exitAssignMode();
        return;
      }
      if (event.key === "Escape") {
        // Staged: hands full, it puts the held thing back and leaves him IN
        // the mode; empty-handed, it leaves. One key doing both jobs cost him
        // the mode for a slip of the hand, and a trip back to the card.
        cableDirectEdit.escapeShapeEdit();
        setSelectedSource(null);
        return;
      }
      // Chrome fires a keydown with no `key` when a datalist suggestion is picked.
      if (
        (event.key ?? "").toLowerCase() !== "z" ||
        !(event.metaKey || event.ctrlKey)
      ) {
        return;
      }
      // Not while he is typing to Karl: there, Ctrl+Z is the textarea's.
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) editableTakeoff.redo();
      else editableTakeoff.undo();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isCableEditActive,
    cableDirectEdit,
    editableTakeoff,
    isAssigning,
    assignSelection.count,
    setAssignSelected,
    exitAssignMode,
  ]);

  // Outside-click closes the source toolbar (a source re-click reopens it).
  useEffect(() => {
    if (!selectedSource) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (sourceToolbarRef.current?.contains(event.target as Node)) return;
      setSelectedSource(null);
    };
    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, [selectedSource]);

  return {
    cablesEnabled,
    cablesForHeader,
    selectedPlanTakeoff,
    cableGaps,
    karlSteps,
    planWalls,
    trayVerdicts,
    deviceSelection,
    clearDeviceSelection,
    assignDeviceSelectionTo,
    circuitNames,
    selectedDeviceMarkers: isAssigning ? undefined : selectedDeviceMarkers,
    assignDeviceBoxes,
    assignMode: {
      isOn: isAssigning,
      showsPanel: assignMode.phase === "on" && assignMode.showsPanel,
      board: circuitBoardLabel,
      circuitId: splittingCircuit,
      missingFeeders,
      pickFeederFor: cableDirectEdit.startPickFeederFor,
      leaveCategoryOut: (category) =>
        editableTakeoff.setCategoryUnbilled(category, true),
      devices: assignableDevices,
      categories: assignCategories,
      category: assignCategory,
      setCategory: setAssignCategory,
      selection: assignSelection,
      selectedKeys: assignMode.phase === "on" ? assignMode.selected : NO_KEYS,
      enter: enterAssignMode,
      exit: exitAssignMode,
      setSelected: setAssignSelected,
      toggleAt: toggleAssignAt,
      selectArea: selectAssignArea,
      assignTo: assignSelectionTo,
      removeSelected: removeAssignSelection,
    },
    isDeviceSpotlight: spotlightDevices !== null,
    setSpotlightDevices,
    confirmDevicePick,
    candidateDeviceMarkers,
    unroutedDeviceMarkers,
    setUnroutedListOpen,
    unroutedSummary,
    cableTrays,
    drawnRouteTypes,
    areTraysShown,
    setTraysShown,
    areAllCablesShown,
    setAllCablesShown,
    areRouteLinesHidden,
    isBoardsStep,
    boardCircuitIds,
    boardSuspects,
    boardUnroutedCount,
    devicesByCircuit,
    circuitLabels,
    highlightTrayType,
    setHighlightTrayType,
    focusedTrayType,
    setFocusedTrayType,
    isCableEditActive,
    editableTakeoff,
    effectiveCableGeometry,
    cableSourceMarkers,
    cableDestinationInfo,
    cableDestinationInfoById,
    cableDirectEdit,
    cableTakeoffPaths,
    selectedCableCircuit,
    setSelectedCableCircuit,
    selectCableCircuit,
    setHoveredCableCircuit,
    highlightMarker,
    setHighlightMarker,
    heightMarks,
    setHeightMarks,
    selectedSource,
    setSelectedSource,
    sourceToolbarRef,
  };
};
